import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DashboardMode, FilterPresetDto } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { filtersKey } from "@/features/dashboard/lib/filters";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import {
  applyFilterPreset,
  presetMatchesCurrentFilters,
} from "@/features/overview/lib/filter-preset-utils";
import {
  selectOverviewDraftKey,
  useOverviewFiltersStore,
} from "@/features/overview/stores/overviewFilters.store";
import { overviewFilterPresetsQueryOptions } from "@/lib/queries/filter-presets.queries";
import { FilterPresetSelectorCore } from "./FilterPresetSelectorCore";

interface OverviewFilterPresetSelectorProps {
  mode: DashboardMode;
  filtersHydrated?: boolean;
  onPresetApplied?: (preset: FilterPresetDto) => void;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  selectPresetId?: string | null;
}

export function OverviewFilterPresetSelector({
  mode,
  filtersHydrated = true,
  onPresetApplied,
  applySignal,
  onPendingPresetChange,
  selectPresetId = null,
}: OverviewFilterPresetSelectorProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const canListPresets = useIamPermission(usePermissions().OVERVIEW_VIEW_PAGE);
  const { data: presets = [] } = useQuery({
    ...overviewFilterPresetsQueryOptions(mode, activeTenantId),
    enabled: canListPresets && Boolean(activeTenantId),
  });
  const overviewDraftKey = useOverviewFiltersStore(selectOverviewDraftKey);
  const globalDraftKey = useDashboardFiltersStore((s) =>
    filtersKey(s.mode === "competitors" ? s.competitorFilters : s.myBrandFilters),
  );
  const filterSignature = `${overviewDraftKey}\u0000${globalDraftKey}`;

  const labels = useMemo(
    () => ({
      placeholder: t("overview.filterPresets.placeholder", "Choose Preset"),
      triggerLabel: t("overview.filterPresets.triggerLabel", "Presets"),
      listHeading: t("overview.filterPresets.listHeading", "Presets"),
      searchPlaceholder: t("overview.filterPresets.searchPlaceholder", "Search presets"),
      emptyMessage: t("overview.filterPresets.empty", "No presets match"),
      listboxLabel: t("overview.filterPresets.listboxLabel", "Filter presets"),
      ariaLabel: t("overview.filterPresets.ariaLabel", "Filter presets"),
      deleteAria: t("overview.filterPresets.deleteAria", "Delete preset"),
    }),
    [t],
  );

  return (
    <FilterPresetSelectorCore<FilterPresetDto, { name: string }>
      surface="overview"
      mode={mode}
      canListPresets={canListPresets}
      labels={labels}
      filtersHydrated={filtersHydrated}
      filterSignature={filterSignature}
      presets={presets}
      applyPreset={applyFilterPreset}
      presetMatchesCurrent={presetMatchesCurrentFilters}
      onPresetApplied={onPresetApplied}
      applySignal={applySignal}
      onPendingPresetChange={onPendingPresetChange}
      allowCreate={false}
      selectPresetId={selectPresetId}
    />
  );
}
